import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'

import { SessionKeepAlive } from '@/app/session-keepalive'
import { OfflineBanner } from '@/components/app/offline-banner'
import { SseRoot } from '@/app/sse-root'
import { ChangePasswordForm, SessionExpiredDialog, useSignOut } from '@/features/auth'
import { isApiError } from '@/lib/api/errors'
import { meQuery } from '@/lib/api/queries/auth'
import { SessionContext } from '@/lib/auth/session-context'

/** Signed-in area: session guard, the user event stream and the session for shared chrome. */
export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ context, location }) => {
    try {
      return { me: await context.queryClient.ensureQueryData(meQuery) }
    } catch (e) {
      if (isApiError(e) && e.status === 401) throw redirect({ to: '/login', search: { redirect: location.href } })
      throw e
    }
  },
  component: AuthLayout,
})

function AuthLayout() {
  const { me: initial } = Route.useRouteContext()
  const { data: me = initial } = useQuery(meQuery)
  const signOut = useSignOut()
  const session = useMemo(() => ({ me, signOut }), [me, signOut])
  if (me.mustChangePassword) {
    // The server refuses everything else until the password is changed (`password_change_required`).
    return <ChangePasswordForm username={me.user.username} onSignOut={() => void signOut()} />
  }
  return (
    <SessionContext value={session}>
      <SseRoot />
      <SessionKeepAlive />
      <div className="flex h-full flex-col">
        <OfflineBanner />
        <div className="min-h-0 flex-1">
          <Outlet />
        </div>
      </div>
      <SessionExpiredDialog username={me.user.username} />
    </SessionContext>
  )
}
